import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import logo from "../../assets/UpdatedMainLogo.png";
import getApiBase from "../../components/getApiBase";
import { formatRatePercent } from "../../utils/platformFee";

const Header = ({ onLogout }) => {
  const navigate = useNavigate();

  return (
    <header
      style={{
        backgroundColor: "#fff",
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
        padding: "10px 20px",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <img src={logo} alt="Autoshops.com Logo" style={{ height: "auto", width: "300px" }} />
        </Box>
        <nav style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button
            onClick={() => navigate("/admin/pricing-dashboard")}
            sx={{ color: "#ea4335", fontWeight: 700, fontSize: "18px" }}
          >
            Pricing Dashboard
          </Button>
          <Button
            onClick={onLogout}
            sx={{ color: "#ea4335", fontWeight: 700, fontSize: "18px" }}
          >
            Logout
          </Button>
        </nav>
      </Box>
    </header>
  );
};

const AdminPlatformFee = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [current, setCurrent] = useState(null);
  const [versions, setVersions] = useState([]);
  const [ratePercent, setRatePercent] = useState("3");
  const [note, setNote] = useState("");

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [currentRes, versionsRes] = await Promise.all([
        fetch(`${getApiBase()}/api/admin/platform-fee/current`, { credentials: "include" }),
        fetch(`${getApiBase()}/api/admin/platform-fee/rates`, { credentials: "include" }),
      ]);

      if (!currentRes.ok || !versionsRes.ok) {
        const err = await currentRes.json().catch(() => ({}));
        throw new Error(err.error || "Failed to load platform fee settings");
      }

      const currentData = await currentRes.json();
      const versionsData = await versionsRes.json();
      setCurrent(currentData);
      setVersions(versionsData.versions || []);
      if (currentData.currentRate != null) {
        setRatePercent(String(Number(currentData.currentRate) * 100));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleLogout = async () => {
    await fetch(`${getApiBase()}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    navigate("/admin");
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    const pct = Number(ratePercent);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      setError("Enter a valid percentage between 0 and 100.");
      setSaving(false);
      return;
    }

    try {
      const res = await fetch(`${getApiBase()}/api/admin/platform-fee/schedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          rate: pct / 100,
          note: note.trim() || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to schedule rate change");
      }

      setSuccess(
        `Scheduled ${formatRatePercent(pct / 100)} for ${data.shopsScheduled} shop(s), effective 1st of next calendar month.`,
      );
      setNote("");
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Header onLogout={handleLogout} />
      <Box sx={{ maxWidth: 960, mx: "auto", p: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
          Platform fee rate
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          TASK-567 — Rate changes apply on the <strong>1st of the next calendar month</strong> for all
          shops (monthly statement boundary).
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Current effective rate
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, color: "#ea4335" }}>
            {formatRatePercent(current?.currentRate ?? 0.03)}
          </Typography>
          {current?.shopsWithPendingChange > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {current.shopsWithPendingChange} shop(s) have a pending rate change scheduled.
            </Typography>
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }} component="form" onSubmit={handleSchedule}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Schedule new rate
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <TextField
              label="New rate (%)"
              type="number"
              inputProps={{ step: "0.01", min: 0, max: 100 }}
              value={ratePercent}
              onChange={(e) => setRatePercent(e.target.value)}
              required
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              fullWidth
              sx={{ flex: 1, minWidth: 240 }}
            />
          </Box>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? "Scheduling…" : "Schedule rate change"}
          </Button>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ p: 1 }}>
            Rate history
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>No rate versions yet.</TableCell>
                  </TableRow>
                ) : (
                  versions.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell>{v.id}</TableCell>
                      <TableCell>{formatRatePercent(v.rate)}</TableCell>
                      <TableCell>{v.note || "—"}</TableCell>
                      <TableCell>
                        {v.created_at
                          ? new Date(v.created_at).toLocaleString()
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </>
  );
};

export default AdminPlatformFee;
